// ══════════════════════════════════════════════════════
//  recentUpdates.js — 项目近况合成层
//
//  输入：surfNews（trending + recent）
//  输出：结构化的近期动态 5-8 条 + 一句话总结 + 关键主题
//
//  设计目的：把 surfNews 原始标题流转化为可读的"项目近况"画像，
//  填充情报板块的 📰 项目近况 卡片 + 供 verdict.js 等下游消费。
//
//  成本：1 次 Claude Sonnet 调用（~$0.02/项目）
//  缓存：随 project_cache 一起持久化
// ══════════════════════════════════════════════════════

import { RD_BASE } from './config';

// 按时间倒序合并 + 去重 + 截断
function preprocessNews(surfNews, maxItems = 20) {
  if (!surfNews) return [];
  const all = [...(surfNews.trending || []), ...(surfNews.recent || [])];
  const seen = new Map();
  for (const item of all) {
    if (!item?.title) continue;
    const key = item.title.toLowerCase().trim();
    if (!seen.has(key)) seen.set(key, item);
  }
  return Array.from(seen.values())
    .sort((a, b) => (b.published_at || 0) - (a.published_at || 0))
    .slice(0, maxItems);
}

// 数字化 timestamp（秒）→ YYYY-MM-DD
function tsToDate(ts) {
  if (!ts) return '';
  const ms = (typeof ts === 'number' ? ts : parseInt(ts, 10)) * 1000;
  if (!ms || ms < 0) return '';
  try { return new Date(ms).toISOString().slice(0, 10); } catch { return ''; }
}

// 让 Claude 从新闻列表里提炼结构化近况
// 失败/无数据返回 null
export async function synthesizeRecentUpdates(projectName, surfNews) {
  const news = preprocessNews(surfNews);
  if (news.length < 2) return null;

  // 序列化喂给 Claude（每条 ≤ 120 字符，控制总 prompt 体积）
  const newsDigest = news.map((n, i) =>
    `[${i + 1}] ${tsToDate(n.published_at) || '?'} src:${n.source || '?'} | ${(n.title || '').slice(0, 150)}`
  ).join('\n');

  const prompt = `你是 Web3 投研分析师。下方是「${projectName}」最近的 ${news.length} 条新闻标题。
请筛选出最有投资价值的 5-8 条，结构化输出为"项目近况"。

新闻列表（最新在前）：
${newsDigest}

## 任务

1. 筛选规则：
   - 优先：产品发布 / 主网上线 / 重要合作 / 融资 / 安全事件 / 监管动态
   - 过滤：纯营销稿、价格波动评论、KOL 推文、媒体转载同源内容
   - 同一事件多源报道只保留 1 条

2. 每条提炼为结构化对象：
   - date: YYYY-MM-DD
   - title: 简洁标题 ≤ 30 字（不是原标题，可改写）
   - sourceIdx: 对应输入列表里的编号（用于 UI 反查原 URL）
   - category: product / partnership / funding / security / milestone / regulatory / other
   - sentiment: positive / neutral / negative（投资人视角）
   - aiComment: 一句话评语 ≤ 60 字，回答"这条对投资判断意义何在"

3. oneLineSummary: 一句话总结整体动态 ≤ 80 字
   例："近 30 天 3 次产品升级 + 1 次顶级 VC 跟投，开发节奏活跃"

4. keyThemes: 2-3 个主题词 ≤ 8 字/个
   例：["跨链", "RWA", "稳定币"]

## 严格规则
- 不要编造：新闻列表里没的事件别加
- 没价值的新闻就跳过，updates 数组可以少于 5 条
- 投资人视角：sentiment="positive" 应该考虑这是否真的对持币人/估值有利

## 输出 JSON 格式（严格）
{
  "updates": [
    {
      "date": "YYYY-MM-DD",
      "title": "≤30 字标题",
      "sourceIdx": 数字（输入列表里 [N] 的 N）,
      "category": "product|partnership|funding|security|milestone|regulatory|other",
      "sentiment": "positive|neutral|negative",
      "aiComment": "≤60 字评语"
    }
  ],
  "oneLineSummary": "≤80 字一句话总结",
  "keyThemes": ["主题1", "主题2"]
}`;

  try {
    const res = await fetch(`${RD_BASE}/api/ai/score`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'claude-opus-4-7',
        max_tokens: 1500,
        messages: [{ role: 'user', content: prompt }],
      }),
    });
    if (!res.ok) {
      console.warn(`[recent-updates-ai] ❌ HTTP ${res.status}`);
      return null;
    }
    const ai = await res.json();
    // Q60
    if (ai._fallback) console.warn(`[recent-updates-ai] ⚠️ DeepSeek 兜底, 原因: ${ai._fallback_reason || '未知'}`);
    else console.log(`[recent-updates-ai] ✅ ${ai.model || 'claude'} OK`);
    const text = (ai.content || []).map(b => b?.type === 'text' ? b.text : '').join('');
    const cleaned = text.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
    const m = cleaned.match(/\{[\s\S]*\}/);
    if (!m) return null;
    let parsed;
    try { parsed = JSON.parse(m[0]); } catch { return null; }

    const updates = Array.isArray(parsed.updates) ? parsed.updates : [];
    // 把 sourceIdx 映射回真实 URL，方便 UI 直接 a href 跳转
    const enriched = updates
      .filter(u => u?.title && u?.date)
      .slice(0, 10)
      .map(u => {
        const idx = parseInt(u.sourceIdx, 10) - 1; // 转 0-based
        const src = (idx >= 0 && idx < news.length) ? news[idx] : null;
        return {
          date:       String(u.date).slice(0, 10),
          title:      String(u.title).slice(0, 80),
          category:   String(u.category || 'other').toLowerCase(),
          sentiment:  ['positive', 'neutral', 'negative'].includes(u.sentiment)
                        ? u.sentiment : 'neutral',
          aiComment:  String(u.aiComment || '').slice(0, 120),
          sourceUrl:  src?.url || '',
          sourceName: src?.source || '',
        };
      });

    return {
      updates:         enriched,
      oneLineSummary:  String(parsed.oneLineSummary || '').slice(0, 120),
      keyThemes:       Array.isArray(parsed.keyThemes)
                         ? parsed.keyThemes.slice(0, 4).map(t => String(t).slice(0, 12)).filter(Boolean)
                         : [],
      totalNewsScanned: news.length,
      generatedAt:     new Date().toISOString(),
    };
  } catch { return null; }
}
